import { useQuery } from '@apollo/client';
import { FC, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import CLOSE_ICON from '../../../../assets/images/close.svg';
import { AppContext } from '../../../../context';
import { buildCommissionSheetRow } from '../../../../utils/commissionSheet';
import { ROLES } from '../../../../utils/constant';
import { copySheetToClipboard } from '../../../../utils/sheetClipboard';
import { GET_DH_PLACEMENT_COMMISSIONS } from '../gql';

// kan-88: the executive tracks commission payouts in an external Excel sheet.
// This modal renders one placement as that sheet's row (columns A–U) and copies
// it tab-separated so a paste lands each value in its own cell.

// kan-95: the source columns (F/G) carry each split user's commission, so the
// sheet is only exportable once every split user on both sides has one.

// kan-98: the column rules themselves live in utils/commissionSheet so the
// accounting dashboard's bulk export builds byte-identical rows.

interface ICommissionSheetExportProps {
  placement: any;
  placementSplitting: Array<any>;
}

const CommissionSheetExport: FC<ICommissionSheetExportProps> = ({
  placement,
  placementSplitting,
}) => {
  const { userRoles } = useContext(AppContext);
  const isExecutive = !!userRoles?.includes(ROLES.EXECUTIVE);

  const [open, setOpen] = useState<boolean>(false);

  // Same document the commissions section on this page uses, so the two share
  // the Apollo cache and an entry added there enables this button immediately.
  const { data: commissionsData } = useQuery(GET_DH_PLACEMENT_COMMISSIONS, {
    variables: { placementId: placement?.id },
    skip: !placement?.id,
    fetchPolicy: 'cache-and-network',
  });

  if (!isExecutive || !placement) return null;

  const toggle = () => setOpen(!open);

  const { columns, commissionsComplete } = buildCommissionSheetRow({
    placement,
    placementSplitting,
    commissions: commissionsData?.dhPlacementCommissions || [],
  });

  const copyRow = async () => {
    // Shares the weekly-activity sheet's clipboard path (kan-97) so both
    // exports behave identically. No cell here is colored, so the helper
    // stays plain-text only and the destination sheet's number formats
    // (percent in R, currency in Q/S/T/U) keep applying on paste.
    const copied = await copySheetToClipboard([
      columns.map((column) => ({ text: column.value })),
    ]);
    if (copied) {
      toast.success('Row copied — paste it into the sheet');
    } else {
      toast.error('Copy failed — please copy the values manually');
    }
  };

  return (
    <>
      <button
        type="button"
        className="buttonGenericStyle filledButton mr-3"
        onClick={toggle}
        disabled={!commissionsComplete}
        style={{ opacity: commissionsComplete ? 1 : 0.5 }}
        title={
          commissionsComplete
            ? undefined
            : 'Enter commissions for all split users first'
        }
      >
        Commission Sheet
      </button>

      <Modal
        isOpen={open}
        toggle={toggle}
        size="xl"
        className="revampDialog"
        style={{ maxWidth: '95vw' }}
      >
        <ModalHeader toggle={toggle}>
          <div className="d-flex align-items-center">
            <span>Commission Sheet Row</span>
          </div>
          <button className="iconButton" onClick={toggle} type="button">
            <img src={CLOSE_ICON} alt="close" />
          </button>
        </ModalHeader>

        <ModalBody>
          {/* One horizontal row, like the sheet itself, scrollable sideways. */}
          <div style={{ overflowX: 'auto' }}>
            <Table
              bordered
              size="sm"
              style={{ width: 'max-content', fontSize: '12px' }}
            >
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.letter}
                      style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}
                    >
                      <small className="text-muted d-block font-weight-normal">
                        {column.letter}
                      </small>
                      {column.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {columns.map((column) => {
                    const shown = column.display ?? column.value;
                    return (
                      <td key={column.letter} style={{ whiteSpace: 'nowrap' }}>
                        {shown || <span className="text-muted">—</span>}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </Table>
          </div>
        </ModalBody>

        <ModalFooter>
          <button type="button" onClick={toggle} className="buttonPrimary mr-3">
            Close
          </button>
          <button
            type="button"
            onClick={copyRow}
            className="buttonGenericStyle filledButton"
          >
            Copy Row
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CommissionSheetExport;
